import { ReactNode } from "react";
import { TourKey, Tours } from "./TourProvider";
import {
  Page,
  Tour,
  TourConditions,
  TourConditionType,
} from "./TourProvider.types";
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  createOrUpdateUserTourDjango,
  getUserToursDjango,
  TourDjango,
} from "./TourProvider.api";
import { EuiButton, EuiButtonEmpty, EuiTourStep } from "@inscopix/ideas-eui";

export type TourStoreState = {
  storedTours: TourDjango[];
  currentTour: null | TourKey;
  currentStep: null | number;
  setCurrentPage: (page: Page | null) => void;
  getCurrentPageOptions: () => Record<string, Tour>;
  tourConditions: Record<TourConditionType, boolean>;
  setTourCondition: (condition: TourConditionType, value: boolean) => void;
  completedTours: TourKey[];
  completeTour: (
    tour: TourKey,
  ) => Promise<{ error?: Error } | { tour: TourDjango }>;
  setTourIncomplete: (
    tour: TourKey,
  ) => Promise<{ error?: Error } | { tour: TourDjango }>;
  startTour: (tour: TourKey) => void;
  closeTour: () => void;
  nextStep(): void;
  getTourOptionsForPage: (page: Page | null) => Record<TourKey, Tour>;
  getTourStepComponentRenderer: (
    tour: TourKey,
    stepNumber: number,
  ) => (
    currentStep: number | null,
    currentTour: TourKey | null,
    additionalAction?: () => void,
  ) => ReactNode;
  currentPage: Page | null;
};

export const createTourStore = async () => {
  const storedTours = await getUserToursDjango();
  const knownTours = storedTours.filter((tour) =>
    Object.keys(Tours).includes(tour.tour_id),
  );
  const completedTourIds = knownTours
    .filter((tour) => tour.completed)
    .map((tour) => tour.tour_id as TourKey);
  return createStore<TourStoreState>()(
    immer((set, get) => ({
      storedTours: knownTours,
      currentTour: completedTourIds.includes("TourNewVersionIntro_25_2")
        ? null
        : "TourNewVersionIntro_25_2",
      currentStep: completedTourIds.includes("TourNewVersionIntro_25_2")
        ? null
        : 0,
      setCurrentPage: (page) => {
        set((state) => {
          state.currentPage = page;
        });
      },
      // initialize tour conditions to false from TourConditions
      tourConditions: TourConditions.reduce(
        (acc, condition) => {
          acc[condition] = false;
          return acc;
        },
        {} as Record<TourConditionType, boolean>,
      ),
      setTourCondition: (condition, value) => {
        set((state) => {
          state.tourConditions[condition] = value;
        });
      },
      currentPage: null,
      completedTours: completedTourIds !== undefined ? completedTourIds : [],
      completeTour: async (tourKey) => {
        // try to find the tour in Tours
        const tour = Tours[tourKey];
        if (tour === undefined) {
          return { error: new Error("Tour not found") };
        }
        // if tour is stored, update it
        const storedTour = get().storedTours.find((t) => t.tour_id === tourKey);
        const { data: updatedTour } = await createOrUpdateUserTourDjango(
          tourKey,
          true,
          false,
          storedTour?.id,
        );
        set((state) => {
          // end the current tour
          state.currentTour = null;
          state.currentStep = null;
          // if tour was successfully updated, update the stored tours
          if (updatedTour !== undefined) {
            const idx = state.storedTours.findIndex(
              (t) => t.id === updatedTour.id,
            );
            // update it if exists
            if (idx !== -1) {
              state.storedTours[idx] = updatedTour;
            }
            // or add it to the list
            else {
              state.storedTours.push(updatedTour);
            }
            // update completed tours
            if (!state.completedTours.includes(tourKey)) {
              state.completedTours.push(tourKey);
            }
          }
        });
        return { tour: updatedTour };
      },
      setTourIncomplete: async (tourKey) => {
        const tour = Tours[tourKey];
        if (tour === undefined) {
          return { error: new Error("Tour not found") };
        }
        const storedTour = get().storedTours.find((t) => t.tour_id === tourKey);
        const { data: updatedTour } = await createOrUpdateUserTourDjango(
          tourKey,
          false,
          false,
          storedTour?.id,
        );
        set((state) => {
          // if tour was successfully updated, update the stored tours
          if (updatedTour !== undefined) {
            const idx = state.storedTours.findIndex(
              (t) => t.id === updatedTour.id,
            );
            // update it if exists
            if (idx !== -1) {
              state.storedTours[idx] = updatedTour;
            }
            // or add it to the list
            else {
              state.storedTours.push(updatedTour);
            }
            // update completed tours
            state.completedTours = state.completedTours.filter(
              (t) => t !== tourKey,
            );
          }
        });
        return { tour: updatedTour };
      },
      startTour: (tourKey) =>
        set((state) => {
          state.currentTour = tourKey;
          state.currentStep = 0;
        }),
      closeTour: () => {
        set((state) => {
          state.currentTour = null;
          state.currentStep = null;
        });
      },
      nextStep: () => {
        set((state) => {
          if (state.currentStep != null) state.currentStep += 1;
        });
      },
      getTourOptionsForPage(pageId) {
        if (pageId === null) return {};
        return Object.entries(Tours).reduce(
          (acc, [key, tour]) => {
            // check if the tour applies to the current page
            if (tour.pages.includes(pageId)) {
              // don't include any tours with conditions that are not met by conditions in store
              const conditionsMet =
                tour.conditions === undefined ||
                Object.entries(tour.conditions).every(
                  ([condition, value]) =>
                    get().tourConditions[condition as TourConditionType] ===
                    value,
                );

              if (conditionsMet) {
                acc[key] = tour;
              }
            }
            return acc;
          },
          {} as Record<string, Tour>,
        );
      },
      // retrieve tours that apply to the current page
      getCurrentPageOptions() {
        const pageId = get().currentPage;
        if (pageId === null) return {};
        return get().getTourOptionsForPage(pageId);
      },
      // get a component renderer for a given tour and step
      getTourStepComponentRenderer(tour, stepNumber) {
        return getTourStepComponentRenderer(
          tour,
          stepNumber,
          get().completeTour,
          get().closeTour,
          () => get().nextStep(),
        );
      },
    })),
  );
};

/*
 * returns a function that renders a tour step component
 * for a given tour and step number
 */
const getTourStepComponentRenderer = (
  tour: TourKey,
  stepNumber: number,
  completeTour: (
    tour: TourKey,
  ) => Promise<{ error?: Error } | { tour: TourDjango }>,
  closeTour: () => void,
  nextStep: () => void,
): ((
  currentStep: number | null,
  currentTour: TourKey | null,
  additionalAction?: () => void,
) => ReactNode) => {
  const step = Tours[tour].steps[stepNumber];
  const totalNumberSteps = Tours[tour].steps.length;
  const isLastStep = stepNumber === totalNumberSteps - 1;
  const hasDismissButton = Tours[tour]?.options?.dismissButton === true;
  const renderStep = (
    currentStep: number | null,
    currentTour: TourKey | null,
    additionalAction?: () => void,
  ) => {
    if (currentStep === null) return null;
    if (currentTour !== tour) return null;
    if (currentStep !== stepNumber) return null;
    const footerAction = isLastStep ? (
      <EuiButton
        color="success"
        size="s"
        onClick={() => {
          void completeTour(tour);
        }}
      >
        Finish tour
      </EuiButton>
    ) : (
      [
        <EuiButtonEmpty
          key={`close-tour-${tour}`}
          size="s"
          color="text"
          onClick={() => {
            if (hasDismissButton) {
              void completeTour(tour);
            } else closeTour();
          }}
        >
          {hasDismissButton ? "Dismiss" : "Close"}
        </EuiButtonEmpty>,
        <EuiButton
          key={`next-tour-${tour}`}
          color="success"
          size="s"
          onClick={() => {
            additionalAction && additionalAction();
            nextStep();
          }}
        >
          Next
        </EuiButton>,
      ]
    );
    return (
      <EuiTourStep
        key={`${tour}-${stepNumber}`}
        step={stepNumber + 1}
        stepsTotal={totalNumberSteps}
        title={step.title}
        content={step.content}
        footerAction={footerAction}
        anchor={`#${step.anchorId}`}
        zIndex={1000}
        onFinish={() => void completeTour(tour)}
        isStepOpen={currentStep === stepNumber}
        maxWidth={400}
        {...step.props}
      />
    );
  };
  return renderStep;
};
